/**
 * Care History — a doctor-ready one-pager built from real data:
 * journey position, service episodes (with dates and contacts), and the
 * dated change timeline. Print-friendly: only the sheet prints.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { JOURNEY_AREAS, SERVICES, currentStageKey } from "@/lib/roadmapStages";
import { Printer, ArrowLeft, FileText } from "lucide-react";

const norm = (s) => (s === "starting_soon" ? "referred" : s);
const STATUS_LABEL = { referred: "Referred", active: "Active", paused: "Paused", ended: "Ended" };

const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
    return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function episodeLines(p) {
    const lines = [];
    (p.history || []).filter(h => h.status === "ended").forEach(h => {
        const bits = [h.start_date ? fmtDate(h.start_date) : null, h.end_date ? fmtDate(h.end_date) : (h.ended_at ? fmtDate(h.ended_at) : null)].filter(Boolean);
        lines.push(`Episode: ${bits.join(" – ") || "dates not entered"}${h.note ? ` (${h.note})` : ""}`);
    });
    return lines;
}

export default function CareHistory() {
    const sid = getSessionId();
    const [profile, setProfile] = useState(null);
    const [intake, setIntake] = useState(null);
    const [providers, setProviders] = useState([]);
    const [events, setEvents] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get(`/profile/${sid}`).catch(() => ({ data: {} })),
            api.get(`/intake/${sid}`).catch(() => ({ data: null })),
            api.get(`/care-team/providers/${sid}`).catch(() => ({ data: { providers: [] } })),
            api.get(`/roadmap/transitions/${sid}`).catch(() => ({ data: { events: [] } })),
        ]).then(([pr, ir, cr, tr]) => {
            setProfile(pr.data || {});
            setIntake(ir.data || null);
            setProviders(cr.data?.providers || []);
            setEvents(tr.data?.events || []);
        }).finally(() => setLoaded(true));
    }, [sid]);

    const primaryKey = profile?.primary_journey_area || (loaded ? currentStageKey({
        predx: intake?.predx, estimated_stage: intake?.estimated_stage,
        has_formal_dx: profile?.has_formal_dx, bereaved: profile?.bereaved,
    }) : null);
    const primaryArea = JOURNEY_AREAS.find(a => a.key === primaryKey);
    const alsoAreas = (profile?.also_dealing_with || [])
        .filter(k => k !== primaryKey)
        .map(k => JOURNEY_AREAS.find(a => a.key === k)?.title)
        .filter(Boolean);

    const groups = useMemo(() => ({
        active: providers.filter(p => norm(p.status) === "active"),
        referred: providers.filter(p => norm(p.status) === "referred"),
        paused: providers.filter(p => norm(p.status) === "paused"),
        ended: providers.filter(p => norm(p.status) === "ended"),
    }), [providers]);

    const sortedEvents = useMemo(
        () => [...events].sort((a, b) => (a.date < b.date ? 1 : -1)),
        [events],
    );

    return (
        <div className="max-w-3xl mx-auto space-y-4 animate-fade-in-up" data-testid="care-history-page">
            <style>{`@media print {
                body * { visibility: hidden !important; }
                #care-history-sheet, #care-history-sheet * { visibility: visible !important; }
                #care-history-sheet { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
            }`}</style>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <Link to="/roadmap" className="inline-flex items-center gap-1.5 text-sm text-teal-dark hover:underline" data-testid="care-history-back">
                    <ArrowLeft size={14} strokeWidth={2} /> Back to roadmap
                </Link>
                <button type="button" onClick={() => window.print()} className="ml-btn-primary text-sm" data-testid="care-history-print">
                    <Printer size={14} strokeWidth={2} /> Print or save as PDF
                </button>
            </div>

            <div id="care-history-sheet" className="ml-card p-6 md:p-8 bg-white" data-testid="care-history-sheet">
                <div className="flex items-start justify-between gap-4 border-b border-cream-dark pb-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark flex items-center gap-1.5">
                            <FileText size={12} strokeWidth={2.2} /> Memory Lane · Care history summary
                        </p>
                        <h1 className="mt-1 text-2xl font-heading font-semibold text-[#12776F]" data-testid="care-history-title">
                            {profile?.loved_one_name ? `Care history for ${profile.loved_one_name}` : "Care history"}
                        </h1>
                        {profile?.caregiver_name && (
                            <p className="text-sm text-[#4C5551]">Prepared by {profile.caregiver_name} (family caregiver)</p>
                        )}
                    </div>
                    <p className="text-xs text-[#6E7A74] shrink-0">Generated {fmtDate(new Date().toISOString())}</p>
                </div>

                <section className="mt-4" data-testid="care-history-journey">
                    <h2 className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Where the family is on the journey</h2>
                    <p className="mt-1 text-sm text-[#1F2624]">
                        {primaryArea ? <><span className="font-semibold">{primaryArea.title}</span> (primary)</> : <span className="italic text-[#6E7A74]">Not set</span>}
                        {alsoAreas.length > 0 && <> · Also dealing with: {alsoAreas.join(", ")}</>}
                    </p>
                </section>

                <section className="mt-5" data-testid="care-history-services">
                    <h2 className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Services and providers</h2>
                    {providers.length === 0 && <p className="mt-1 text-sm text-[#6E7A74] italic">No services on file yet.</p>}
                    {[["active", "Current"], ["referred", "Referred / planned"], ["paused", "Paused"], ["ended", "Past"]].map(([key, label]) => groups[key].length > 0 && (
                        <div key={key} className="mt-2.5">
                            <p className="text-[11px] font-bold text-[#1F2624] uppercase tracking-wide">{label}</p>
                            <ul className="mt-1 space-y-1.5">
                                {groups[key].map(p => {
                                    const eps = episodeLines(p);
                                    return (
                                        <li key={p.id} className="text-sm text-[#1F2624] border-b border-cream-dark/50 pb-1.5" data-testid={`care-history-svc-${p.id}`}>
                                            <span className="font-semibold">{p.name}</span>
                                            <span className="text-[#4C5551]"> · {SERVICES[p.service_key]?.name || p.service_name || "Service"} · {STATUS_LABEL[norm(p.status)]}</span>
                                            <span className="block text-xs text-[#4C5551]">
                                                {p.start_date ? `${eps.length && norm(p.status) !== "ended" ? "Restarted" : "Started"} ${fmtDate(p.start_date)}` : "Start date not entered"}
                                                {p.end_date ? ` · Ended ${fmtDate(p.end_date)}` : ""}
                                                {p.phone ? ` · ${p.phone}` : ""}
                                                {p.contact_person ? ` · Contact: ${p.contact_person}` : ""}
                                            </span>
                                            {p.notes && <span className="block text-xs text-[#4C5551]">Why involved: {p.notes}</span>}
                                            {p.note && <span className="block text-xs text-[#4C5551]">Note: {p.note}</span>}
                                            {eps.map((ln, i) => <span key={i} className="block text-xs text-[#6E7A74] italic">{ln}</span>)}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </section>

                <section className="mt-5" data-testid="care-history-timeline">
                    <h2 className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Recorded changes (most recent first)</h2>
                    {sortedEvents.length === 0 && <p className="mt-1 text-sm text-[#6E7A74] italic">No changes recorded yet.</p>}
                    <ul className="mt-1 space-y-1.5">
                        {sortedEvents.map(ev => {
                            const area = JOURNEY_AREAS.find(a => a.key === ev.journey_area);
                            return (
                                <li key={ev.id} className="text-sm text-[#1F2624]" data-testid={`care-history-event-${ev.id}`}>
                                    <span className="font-semibold">{fmtDate(ev.date)}</span>
                                    <span> — {ev.label}</span>
                                    {area && <span className="text-[#4C5551]"> (during {area.title})</span>}
                                    {ev.notes && <span className="block text-xs text-[#4C5551] pl-4">{ev.notes}</span>}
                                </li>
                            );
                        })}
                    </ul>
                </section>

                <p className="mt-6 pt-3 border-t border-cream-dark text-[10px] text-[#6E7A74]">
                    Prepared with Memory Lane Care Navigator from caregiver-entered information. This summary is
                    personalized guidance, not a clinical record. Please verify details with the family.
                </p>
            </div>
        </div>
    );
}
